import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import type { SrpProductData } from "~/utils/models";
import { forwardRef, type ReactNode } from "react";
import { ProductContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { Link, LinkType } from "~/common/components/Link";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { SideButton } from "~/common/components/Button/SideButton";
import { Button, styled } from "~/common/mui";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { canSeeAddToCartButton } from "~/utils/features";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { closeModalById } from "~/common/utils/ModalService";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { useTranslation } from "react-i18next";
import { EwellixRequestAQuoteButton } from "~/features/Ewellix/components/EwellixRequestAQuoteButton";
import { resolveProductUrlAutosuggest } from "~/features/search/components/Autosuggest/SearchRelatedResults/ProductItem/ProductItem";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { isCartPage } from "~/utils/url/urlUtils";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CadIcon from "~/common/mui/icons/Cad";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CalculatorIcon from "~/common/mui/icons/Calculator";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "~/app/store";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

interface Props {
  className?: string;
  product: SrpProductData;
  query?: string;
  hide: () => void;
}

export const ProductItem = forwardRef<HTMLAnchorElement, Props>(
  ({ product, className, query = "", hide }: Props, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const searchAvailabilityEnabled = useKameleoonFlag(KAMELEOON_FLAGS.searchAvailability);

    const hasSales = Object.keys(product.salesCampaign).length > 0;

    const pdpUrl = resolveProductUrlAutosuggest(product);

    const linkProps = {
      onClick: () => saveSearchTerm(product.designation, pdpUrl),
      href: pdpUrl,
      ["tracking-aux-sch-prod-name"]: product.designation,
      ["tracking-aux-sch-prod-id"]: product.sapMaterialNumber,
      ["data-prod-article"]: product.hybrisCode,
    };

    const [addToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({});

    const addToCart = (e: React.MouseEvent, productCode: string, restrictionQuery: string) => {
      e.preventDefault();
      e.stopPropagation();
      closeModalById("add-to-cart-dialog");
      return AddToCartService.openAddToCartWizard(
        {
          productCode: productCode,
          restrictionQuery: restrictionQuery,
          source: "autocomplete",
          onSuccessCallback: () => {
            if (isCartPage()) {
              dispatch(cartPageLoadThunk());
            }
          },
        },
        {
          page: "unknown",
        },
        dispatch,
      );
    };

    const screenSize = useNumericScreenSize();
    const isMobile = screenSize <= useNumericScreenSize.phone;
    const canSeeAddToCart = canSeeAddToCartButton();
    const productDescriptor = fakeDescriptorFromProduct(product);

    let buttons;
    if ((product.externalBrand || userIsCustomer()) && isProductPMA(productDescriptor)) {
      buttons = <EwellixRequestAQuoteButton product={product} />;
    } else if (canSeeAddToCart) {
      const commonProps = {
        ["tracking-aux-sch-addtc-prod-name"]: product.designation,
        ["tracking-aux-sch-addtc-prod-id"]: product.sapMaterialNumber,
        onClick: (e: React.MouseEvent) => {
          addToCart(e, product.code, query).then(() => hide());
        },
      };
      if (isMobile) {
        buttons = <SideButton color="primary" icon={<ShoppingCartIcon sx={{ fontSize: 24 }} />} {...commonProps} />;
      } else {
        buttons = (
          <Button size="medium" startIcon={<ShoppingCartIcon />} {...commonProps}>
            {t("recentlyViewProducts.addProducts")}
          </Button>
        );
      }
    } else {
      const addToWishlistBtn = (
        <SideButton
          onClick={() => addToWishlist(product)}
          color="primary"
          icon={<BookmarkIcon sx={{ fontSize: 24 }} />}
        />
      );
      if (isMobile) {
        buttons = addToWishlistBtn;
      } else {
        buttons = (
          <>
            <SideButton
              href={product.cadUrl}
              className={"scha__autosuggest-side-button"}
              color="primary"
              icon={<CadIcon sx={{ fontSize: 24 }} />}
              disabled={!product.cadUrl}
            />
            {addToWishlistBtn}
            <SideButton
              href={product.calcUrl}
              className={"scha__autosuggest-side-button"}
              color="primary"
              icon={<CalculatorIcon sx={{ fontSize: 24 }} />}
              disabled={!product.calcUrl}
            />
          </>
        );
      }
    }

    return (
      <ProductContext value={product}>
        <ProductItemView
          className={className}
          hasSales={hasSales}
          campaignCode={Object.values(product.salesCampaign)[0]?.[0].code}
          image={<Product.PrimaryImage linkProps={{ ...linkProps, tabIndex: -1 }} />}
          designation={
            <>
              <Product.PreferredIndicator noMargin />
              <Link {...linkProps} ref={ref} variant={LinkType.NAVIGATION}>
                <TextWithHighlight highlight={query} highlightClass={"f-normal"}>
                  {product.designation}
                </TextWithHighlight>
              </Link>

              {hasSales && <SaleTag size={16} />}
            </>
          }
          description={<Product.ShortDescription linkProps={{ ...linkProps, tabIndex: -1 }} />}
          availability={searchAvailabilityEnabled && <Product.StockIndicator />}
          buttons={buttons}
        />
        {addToWishlistModal}
      </ProductContext>
    );
  },
);

interface ProductItemViewProps {
  className?: string;
  hasSales?: boolean;
  campaignCode?: string;
  image: ReactNode;
  designation: ReactNode;
  description: ReactNode;
  availability?: ReactNode;
  buttons?: ReactNode;
}

export const ProductItemView = ({
  className,
  hasSales = false,
  campaignCode = "None",
  image,
  designation,
  description,
  availability,
  buttons,
}: ProductItemViewProps) => {
  return (
    <ProductItemRoot
      className={className}
      asgsta-product-parent-marker="true"
      asgsta-is-campaign-belonging={`${hasSales}`}
      asgsta-campaign={campaignCode ?? "None"}
      data-zoovu="searchSuggestion"
    >
      <ImageSlot>{image}</ImageSlot>
      <DesignationContainer>{designation}</DesignationContainer>
      <DescriptionSlot>{description}</DescriptionSlot>
      {availability && <AvailabilitySlot>{availability}</AvailabilitySlot>}
      <Buttons>{buttons}</Buttons>
    </ProductItemRoot>
  );
};

const truncatedContent = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const ProductItemRoot = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  display: "grid",
  gridTemplate: `"image designation buttons" 1fr "image description buttons" 1fr "image availability buttons" 1fr / ${theme.spacing(
    8,
  )} minmax(0, auto) 1fr`,
  columnGap: theme.spacing(2),
  "& a": {
    overflow: "hidden",
  },
  ".scha__autosuggest-side-button": {
    minHeight: "unset !important",
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
  },
}));

const ImageSlot = styled("div")(({ theme }) => ({
  gridArea: "image",
  height: theme.spacing(8),
  width: theme.spacing(8),
  "img, a": {
    objectFit: "contain",
    height: "100%",
    width: "100%",
  },
}));

const DesignationContainer = styled("div")(({ theme }) => ({
  gridArea: "designation",
  alignSelf: "flex-end",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  ...theme.typography.bodyMedium,
  span: {
    ...truncatedContent,
    "> a": {
      display: "inline",
    },
  },
}));

const Buttons = styled("div")({
  gridArea: "buttons",
  justifySelf: "flex-end",
  alignSelf: "center",
});

const DescriptionSlot = styled("div")(({ theme }) => ({
  gridArea: "description",
  alignSelf: "flex-start",
  display: "inline-flex",
  ...truncatedContent,
  ...theme.typography.bodySmall,
  a: {
    overflow: "hidden",
    "> span": truncatedContent,
  },
}));

const AvailabilitySlot = styled("div")({
  gridArea: "availability",
});
